import { detectTableGrid } from "../services/tableGridDetector";
import { useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import type { LevelRow } from "../types/survey";
import type { OCRRowConfidence } from "../types/ocr";

import ImageCropEditor from "../components/ImageCropEditor";
import OCRValidationPanel from "../components/OCRValidationPanel";
import OCRErrorDashboard from "../components/OCRErrorDashboard";

import { parseFieldBookText } from "../services/fieldBookParser";
import { mergeMultiPageSurveyRows } from "../services/multiPageSurveyMerger";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString();

type Props = {
  mode: "levelbook" | "ogl";
  onImportRows: (rows: LevelRow[]) => void;
};

export default function OCRImport({
  mode,
  onImportRows
}: Props) {

  const [images, setImages] = useState<File[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [previewUrl, setPreviewUrl] = useState("");

  const [ocrText, setOcrText] = useState("");

  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState<LevelRow[]>([]);

  const [confidence, setConfidence] =
    useState<number | null>(null);

  const [rowConfidence, setRowConfidence] =
    useState<Record<string, number>>({});

  const [ocrConfidenceRows, setOcrConfidenceRows] =
    useState<OCRRowConfidence[]>([]);
const [gridCells, setGridCells] = useState(0);

const [recognizedRowsCount, setRecognizedRowsCount] = useState(0);

const [oglRowsCount, setOglRowsCount] = useState(0);
  const [rotation, setRotation] =
    useState(0);

  const handleFiles = (files: FileList) => {

    const selected = Array.from(files);

    setImages(selected);

    setPreviewUrl(
      selected.length
        ? URL.createObjectURL(selected[0])
        : ""
    );

    setRows([]);

    setConfidence(null);

    setOcrText("");
  };

  async function pdfToImages(
    file: File
  ): Promise<File[]> {

    const buffer =
      await file.arrayBuffer();

    const pdf =
      await pdfjsLib
        .getDocument({
          data: buffer
        })
        .promise;

    const result: File[] = [];

    for (
      let page = 1;
      page <= pdf.numPages;
      page++
    ) {

      const pdfPage =
        await pdf.getPage(page);

      const viewport =
        pdfPage.getViewport({
          scale: 5
        });

      const canvas =
        document.createElement("canvas");

      const ctx =
        canvas.getContext("2d");

      if (!ctx) continue;

      canvas.width =
        viewport.width;

      canvas.height =
        viewport.height;

      await pdfPage.render({
        canvas,
        canvasContext: ctx,
        viewport
      }).promise;

      const blob =
        await new Promise<Blob>(
          (resolve, reject) => {

            canvas.toBlob((b) => {

              if (!b) {
                reject(
                  new Error(
                    "PDF render failed"
                  )
                );
                return;
              }

              resolve(b);

            }, "image/png");

          }
        );

      result.push(
        new File(
          [blob],
          `page-${page}.png`,
          {
            type: "image/png"
          }
        )
      );
    }

    return result;
  }
const runOCR = async () => {

  let filesToProcess = images;

  if (pdfFile) {
    setOcrText("Converting PDF...");
    filesToProcess = await pdfToImages(pdfFile);
  }

  if (filesToProcess.length === 0) {
    alert("Please select image(s) or PDF first");
    return;
  }

  try {

    setLoading(true);

    setRows([]);
    setOcrText("");

    const pageResults: LevelRow[][] = [];

    let combinedText = "";

    let totalConfidence = 0;
    let confidenceCount = 0;

    for (let i = 0; i < filesToProcess.length; i++) {

      setOcrText(
        `Reading page ${i + 1} of ${filesToProcess.length}...`
      );

      const imageUrl =
        URL.createObjectURL(filesToProcess[i]);
console.log("BEFORE detectTableGrid");

const boxes =
  await detectTableGrid(
    filesToProcess[i]
  );

console.log("AFTER detectTableGrid");
console.log(boxes);
console.log(
  "Detected table cells:",
  boxes.length
);

console.table(boxes);
      const result =
        await Tesseract.recognize(
          imageUrl,
          "eng",
          {
            logger: (m) => {

              if (m.status === "recognizing text") {

                setOcrText(
                  `Page ${i + 1}: ${Math.round(
                    m.progress * 100
                  )}%`
                );

              }

            }
          }
        );

      URL.revokeObjectURL(imageUrl);

      const text = result.data.text;

      combinedText +=
        "\n\n====================\n" +
        "PAGE " +
        (i + 1) +
        "\n====================\n" +
        text;

      if (result.data.confidence) {

        totalConfidence +=
          result.data.confidence;

        confidenceCount++;

      }

      console.log(
        "RAW OCR PAGE",
        i + 1
      );

      console.log(text);

      const parsedRows =
        parseFieldBookText(text);

      console.log(
        "PARSED ROWS",
        parsedRows.length
      );

      console.table(parsedRows);

      pageResults.push(parsedRows);
}
    const mergedRows =
      mergeMultiPageSurveyRows(pageResults);

    console.log(
      "MERGED ROWS",
      mergedRows.length
    );

    console.table(mergedRows);

    const avgConfidence =
      confidenceCount > 0
        ? totalConfidence / confidenceCount
        : 0;

    const confidenceMap: Record<string, number> = {};

    const confidenceRows: OCRRowConfidence[] = [];

    mergedRows.forEach((row) => {

      confidenceMap[row.id] = avgConfidence;

      confidenceRows.push({
        rowId: row.id,
        overall: avgConfidence,
        fields: {
          chainage: avgConfidence,
          offset: avgConfidence,
          side: avgConfidence,
          bs: avgConfidence,
          is: avgConfidence,
          fs: avgConfidence,
          tbmValue: avgConfidence,
          remarks: avgConfidence
        }
      });

    });

    setConfidence(
      confidenceCount > 0
        ? avgConfidence
        : null
    );

    setRowConfidence(confidenceMap);

    setOcrConfidenceRows(confidenceRows);

    setRows(mergedRows);

    setRecognizedRowsCount(
      mergedRows.filter(
        r => r.bs || r.is || r.fs
      ).length
    );

    setOcrText(combinedText);

  } catch (error) {

    console.error("OCR ERROR:", error);

    const err = error as Error;

    setOcrText(
      err?.stack ||
      err?.message ||
      String(error)
    );

  } finally {

    setLoading(false);

  }

};

const exportOCRExcel = async () => {
  const workbook = new ExcelJS.Workbook();

  const sheet = workbook.addWorksheet('OCR Rows');

  sheet.columns = [
    { header: 'Chainage', key: 'chainage', width: 15 },
    { header: 'Offset', key: 'offset', width: 12 },
    { header: 'Side', key: 'side', width: 10 },
    { header: 'BS', key: 'bs', width: 10 },
    { header: 'IS', key: 'is', width: 10 },
    { header: 'FS', key: 'fs', width: 10 },
    { header: 'TBM RL', key: 'tbmValue', width: 12 },
    { header: 'Remarks', key: 'remarks', width: 15 }
  ];

  rows.forEach((row) => {
    sheet.addRow({
      chainage: row.chainage,
      offset: row.offset,
      side: row.side,
      bs: row.bs,
      is: row.is,
      fs: row.fs,
      tbmValue: row.tbmValue,
      remarks: row.remarks
    });
  });

  sheet.getRow(1).font = { bold: true };

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer]),
    'ocr-level-sheet.xlsx'
  );
};

  return (
    <div className="panel">
     <h1>OCR Import Module</h1>
      <h3>
        Current Mode:
        {mode === 'levelbook'
          ? ' Level Book OCR'
          : ' Cross Section / OGL OCR'}
      </h3>
      <p>Upload one or multiple handwritten level book photos and extract text.</p>

      <div style={{ marginBottom: '15px' }}>
        <label>
          <strong>📷 Take Photo</strong>
          <br />

          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
            }}
          />
        </label>

        <br />
        <br />

        <label>
          <strong>🖼 Select From Gallery</strong>
          <br />

          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
            }}
          />
        </label>
<br />
<br />

<label>
  <strong>📄 Select PDF</strong>
  <br />

  <input
    type="file"
    accept=".pdf,application/pdf"
    onChange={(e) => {
      const file = e.target.files?.[0];

      if (file) {
        setPdfFile(file);
        setImages([]);
        setPreviewUrl('');
        setOcrText('');
        setRows([]);
      }
    }}
  />
</label>
      </div>

      <p>Selected pages: {images.length}</p>
    {previewUrl && (
  <>
    <img
      src={previewUrl}
      alt="Preview"
      style={{ maxWidth: '100%' }}
    />

    <ImageCropEditor
      imageUrl={previewUrl}
      onCrop={(blob) => {
        const file = new File(
          [blob],
          'cropped.png',
          { type: 'image/png' }
        );

        setImages([file]);
        setPreviewUrl(
          URL.createObjectURL(file)
        );
      }}
    />
  </>
)}

  <br />
  <br />

  <div style={{ marginBottom: '10px' }}>

    <strong>Rotation:</strong>

    <button type="button" onClick={() => setRotation(0)}>
      0°
    </button>

    <button type="button" onClick={() => setRotation(90)}>
      90°
    </button>

    <button type="button" onClick={() => setRotation(180)}>
      180°
    </button>

    <button type="button" onClick={() => setRotation(270)}>
      270°
    </button>

  </div>

  <button onClick={runOCR} disabled={loading}>
    {loading ? 'Processing...' : 'Process OCR'}
  </button>
{confidence !== null && (
  <h3>
    OCR Confidence: {confidence.toFixed(1)}%
  </h3>
)}
    {gridCells > 0 && (
  <h3>
    Detected Table Cells: {gridCells}
  </h3>
)}

{recognizedRowsCount > 0 && (
  <h3>
    Recognized BS/IS/FS Rows: {recognizedRowsCount}
  </h3>
)}
      {oglRowsCount > 0 && (
  <h3>
    Extracted OGL Rows: {oglRowsCount}
  </h3>
)}
    
      <h3>OCR Result</h3>

      <textarea
        value={ocrText}
        readOnly
        rows={18}
        style={{
          width: '100%',
          padding: '10px',
          fontFamily: 'monospace'
        }}
      />

      <h3>Generated Rows: {rows.length}</h3>
<OCRValidationPanel
  rows={rows}
  onChangeRows={setRows}
/>

      <OCRErrorDashboard rows={rows} />
      <br />
<button
  type="button"
  onClick={exportOCRExcel}
  disabled={rows.length === 0}
>
  Export OCR Excel
</button>

<br />
<br />

<button
  onClick={() => onImportRows(rows)}
  disabled={rows.length === 0}
>
  Import To Level Sheet
</button>
    </div>
  );
}
